import { faTh } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import { StrollerAllReviewsPhotos } from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos";
import {
    strollerAllReviewsPhotosCheckHideThumbs,
    strollerAllReviewsPhotosFetchTrigger,
    strollerAllReviewsPhotosToggleThumbsTrigger,
} from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.actions";
import { strollersAllReviewsPhotosReducer } from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.reducers";
import strollersAllReviewsPhotosSaga from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.sagas";
import {
    getStrollerAllReviewsPhotosState,
    getStrollersAllReviewsPhotosState,
} from "@/mk2/apps/strollers/containers/StrollerAllReviewsPhotos/StrollerAllReviewsPhotos.selectors";
import { strollersAllReviewsGalleryUrl, strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { cacheLast } from "@/mk2/helpers/cache";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, PageRouteProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewPhotoSchema,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    GALLERY,
    GALLERY_THUMBNAILS,
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

import styles from "./StrollerAllReviewsPhotos.mscss";

export const STROLLER_ALL_REVIEWS_PHOTOS_HIDE_THUMBNAILS_COOKIE =
    "_strollerAllReviewsPhotosHideThumbnails";

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    photoId: number;
    loadingState: LoadingState;
    errorMessage: string;
    stroller: StrollerEntity;
    strollerReviews: StrollerUserReviewEntity[];
    strollerReviewsPhotos: StrollerUserReviewPhotoEntity[];
    strollerReviewsPhoto: StrollerUserReviewPhotoEntity;
    hideThumbnails: boolean;
    thumbsKey: string;
}

export interface DispatchProps {
    onLoad(slug: string, photoId: number);
    onCheckHideThumbnails();
    onToggleThumbnails();
}

export type RouteProps = PageRouteProps<{
    slug: string;
    photoId: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerAllReviewsPhotosPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private handleOnExit = cacheLast();

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.routeParams.slug;
            const photoId = parseInt(ownProps.routeParams.photoId, 10);

            const pagesState = getStrollersAllReviewsPhotosState(state);
            const photosState = getStrollerAllReviewsPhotosState(state, slug);
            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                photosState.strollerId,
            );
            const strollerReviews = getDenormalizedEntities<StrollerUserReviewEntity>(
                state,
                StrollerUserReviewSchema,
                photosState.strollerReviewIds,
            );
            const strollerReviewsPhotos = getDenormalizedEntities<StrollerUserReviewPhotoEntity>(
                state,
                StrollerUserReviewPhotoSchema,
                photosState.strollerReviewPhotoIds,
            );
            const strollerReviewsPhoto = strollerReviewsPhotos.find(
                (p) => p.photoFile.id === photoId,
            );

            const loadingState =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.loadingState
                    : strollerReviewsPhoto
                      ? LoadingState.SUCCESS
                      : LoadingState.FAILURE;
            const errorMessage =
                photosState.loadingState != LoadingState.SUCCESS
                    ? photosState.errorMessage
                    : strollerReviewsPhoto
                      ? null
                      : "NOT FOUND";

            const hideThumbnails = pagesState.hideThumbnails;
            const thumbsKey = pagesState.thumbsKey;

            return {
                strollerSlug: slug,
                photoId,
                loadingState,
                errorMessage,
                stroller,
                strollerReviews,
                strollerReviewsPhotos,
                strollerReviewsPhoto,
                hideThumbnails,
                thumbsKey,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerAllReviewsPhotosFetchTrigger,
            onCheckHideThumbnails: strollerAllReviewsPhotosCheckHideThumbs,
            onToggleThumbnails: strollerAllReviewsPhotosToggleThumbsTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerAllReviewsPhotos {...props} />;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { routeNavigate, onToggleThumbnails, strollerSlug } = props;
        return (
            <>
                <span className={styles.StrollerAllReviewsPhotos__faThHack}>
                    <FontAwesomeIcon icon={faTh} />
                </span>
                <Btn
                    type={BtnType.BlueText}
                    label={GALLERY_THUMBNAILS}
                    onClick={onToggleThumbnails}
                />
                <Btn
                    type={BtnType.BlueText}
                    icon={faTh}
                    label={GALLERY}
                    onClick={this.handleOnExit(() => {
                        const gotoUrl = url(strollersAllReviewsGalleryUrl, {
                            slug: strollerSlug,
                        });
                        routeNavigate(gotoUrl, { replace: true });
                    }, strollerSlug)}
                />
            </>
        );
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return false;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug } = props;
        return url(strollersReviewsUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers - Top 02 - Desktop",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.strollers.strollersAllReviewsPhotos",
            strollersAllReviewsPhotosReducer,
        );
        store.injectSaga(
            "containers.strollers.strollersAllReviewsPhotos",
            strollersAllReviewsPhotosSaga,
        );
    }
}
